import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { DEFAULT_FEATURE_ICON, NO_FEATURE_ICON } from '@repo/lib';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { CheckIcon, PlusIcon as PlusIconSolid, StarIcon } from '@heroicons/react/20/solid';

import useCheckoutState from '../../../hooks/useCheckoutState';
import useSelectedPrice from '../../../hooks/useSelectedPrice';
import useCheckoutOrderBumpFormHelpers from '../../../hooks/useCheckoutOrderBumpFormHelpers';
import getOrderBumpOffers from '../../../lib/getOrderBumpOffers';
import getEligibleOrderBumpOffers, {
  BUMP_QUANTITY,
  getMaxBumpQuantity,
  getSoldOutOrderBumpOffers,
} from '../../../lib/getEligibleOrderBumpOffers';
import settleOrderBumps from '../../../lib/settleOrderBumps';
import fillOrderBumpAmounts, {
  getBumpSuggestedPrice,
  getDefaultBumpAmount,
  isBlankAmount,
} from '../../../lib/fillOrderBumpAmounts';
import isOrderBumpHidden from '../../../lib/isOrderBumpHidden';
import hasStock from '../../../lib/hasStock';
import sanitizeCalloutHtml from '../../../lib/sanitizeCalloutHtml';
import resolveInheritedEnabled from '../../../lib/resolveInheritedEnabled';
import getThemeLabels from '../../../lib/getThemeLabels';
import { getPwywPlaceholder } from '../../../lib/getPwywPlaceholder';
import { formatCentsToDecimals, formatPrice } from '../../../services/formatPrice';
import getSubscriptionIntervalWithSlash from '../../../services/translation/getSubscriptionIntervalWithSlash';
import { getImageSrcAndSrcSet } from '../../../services/getUrlFromCdn';
import StyledElement from '../../StyledElement';
import Badge from '../../Badge';
import BadgeIcon, { isBadgeIcon } from '../../BadgeIcon';
import MultilineText from '../../MultilineText';
import QuantityButton from './QuantityButton';
import QuantityField from './QuantityField';
import { TextInput } from '../../Form';
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';

const noop = () => {};

// The thumbnail boxes, keyed by the theme value. `requestWidth` is what the
// srcset asks the CDN for — the helper adds a 2x entry of its own on top — so a
// large thumbnail is never a small one upscaled. Full width has no fixed box,
// so it asks for a width wide enough for any card.
// The box is capped at the card's content: a grid column can be narrower than
// it, and an uncapped picture spilled into the card's padding. The text column
// takes what is left of the row, so this is what splits the card between the
// two.
const THUMBNAIL_SIZES = {
  64: { modifier: 'sm', requestWidth: 64, className: 'w-[64px] max-w-full' },
  128: { modifier: 'md', requestWidth: 128, className: 'w-[128px] max-w-full' },
  192: { modifier: 'lg', requestWidth: 192, className: 'w-[192px] max-w-full' },
  full: { modifier: 'full', requestWidth: 640 },
};
const DEFAULT_THUMBNAIL_SIZE = 64;

// The shapes the card offers. `heightFactor` is what the srcset asks the CDN
// for alongside the width, so the picture that arrives already suits the box
// rather than being cropped twice. A null modifier means no box at all: the
// picture keeps its own proportions.
const ASPECT_RATIOS = {
  square: { modifier: 'square', heightFactor: 1, className: 'aspect-square' },
  landscape: { modifier: 'landscape', heightFactor: 3 / 4, className: 'aspect-[4/3]' },
  wide: { modifier: 'wide', heightFactor: 9 / 16, className: 'aspect-video' },
  // No box and no requested height: the picture arrives as it was uploaded.
  original: { modifier: null, heightFactor: null },
};

// What the browser treats as interactive content inside a <label> — the clicks
// a card must not read as "take one more of this".
const INTERACTIVE_CARD_CONTENT = 'a, button, summary, details, input, select, textarea, label';

/** The offers the buyer actually SEES: eligible, minus the hidden ones. */
export function getVisibleOrderBumpOffers({
  page,
  product,
  selectedPrice,
  ticketGroups,
  orderBumps,
  query,
}) {
  return getEligibleOrderBumpOffers({
    page,
    product,
    selectedPrice,
    ticketGroups,
    orderBumps,
  }).filter(offer => !isOrderBumpHidden(offer, query));
}

function getSoldOutIds({ page, product, selectedPrice, ticketGroups, orderBumps, query }) {
  return new Set(
    getSoldOutOrderBumpOffers({ page, product, selectedPrice, ticketGroups, orderBumps })
      .filter(offer => !isOrderBumpHidden(offer, query))
      .map(offer => String(offer.productId)),
  );
}

/**
 * What the live checkout renders: the shown offers, plus the sold-out ones the
 * buyer would otherwise see, in placement order. Sold-out cards are marked so
 * they render as sold out and cannot be taken.
 */
export function getCheckoutOrderBumpOffers(args) {
  const { page, product } = args;
  const shownIds = new Set(getVisibleOrderBumpOffers(args).map(offer => String(offer.productId)));
  const soldOutIds = getSoldOutIds(args);

  return getOrderBumpOffers(page, product, page?.orderBumpProducts)
    .filter(
      offer => shownIds.has(String(offer.productId)) || soldOutIds.has(String(offer.productId)),
    )
    .map(offer => ({
      offer,
      isShown: true,
      isSoldOut: soldOutIds.has(String(offer.productId)),
    }));
}

/**
 * What the editor's preview renders: every offer, each flagged with whether the
 * live checkout would show it, and sold-out ones flagged as the checkout shows them.
 *
 * Mirrors Variants.js — a variant the buyer would not see is dropped on the real
 * checkout but rendered at `opacity-25` in the preview, so the seller can see
 * the thing they just configured instead of an empty panel. Form state is still
 * driven by the SHOWN set (getVisibleOrderBumpOffers), so a dimmed bump is
 * never ticked or submitted.
 */
export function getPreviewOrderBumpOffers({
  page,
  product,
  selectedPrice,
  ticketGroups,
  orderBumps,
  query,
}) {
  const offers = getOrderBumpOffers(page, product, page?.orderBumpProducts);
  if (!offers.length) return [];

  const shownIds = new Set(
    getVisibleOrderBumpOffers({
      page,
      product,
      selectedPrice,
      ticketGroups,
      orderBumps,
      query,
    }).map(offer => String(offer.productId)),
  );

  const soldOutIds = getSoldOutIds({
    page,
    product,
    selectedPrice,
    ticketGroups,
    orderBumps,
    query,
  });

  // A dimmed card still says sold out, so a hidden or rule-gated bump reads the
  // way the buyer will meet it once revealed.
  return offers.map(offer => {
    const productId = String(offer.productId);
    return {
      offer,
      isShown: soldOutIds.has(productId) || shownIds.has(productId),
      isSoldOut: !hasStock(offer.product),
    };
  });
}

/**
 * The card's pieces, kept separate from their arrangement, so a second layout
 * is a second layout component rather than a rewrite of the card. Each piece
 * renders null when its copy is unset — an absent CTA or description leaves the
 * card exactly as it was.
 */
/**
 * The price picker's selection circle, over a real checkbox.
 *
 * The circle is the price picker's own element so a bump reads like a price
 * option rather than a form field. The input underneath stays a checkbox, not a
 * radio: bumps are independent toggles, and the semantics have to say so even
 * when the mark is round. The circle is decorative — `aria-hidden` — because the
 * input carries the state.
 */
const BUMP_CONTROL_VAR_PREFIX = 'checkout-form-orderBumps';
const BUMP_CONTROL_VARS = [
  'switch-size',
  'switch-background-color',
  'switch-background-color-selected',
  'switch-color',
  'indicator-size',
  'indicator-border-radius',
  'indicator-border-width',
  'indicator-border-color',
  'indicator-border-color-selected',
  'indicator-background-color',
  'indicator-background-color-selected',
  'indicator-color',
];

// Tailwind only builds classes it finds written whole, so another owner repoints
// the order bump's variable names at its own for this subtree.
function getControlVarOverrides(varPrefix) {
  if (varPrefix === BUMP_CONTROL_VAR_PREFIX) return undefined;
  return Object.fromEntries(
    BUMP_CONTROL_VARS.map(name => [
      `--cp_${BUMP_CONTROL_VAR_PREFIX}-${name}`,
      `var(--cp_${varPrefix}-${name})`,
    ]),
  );
}

export function BumpIndicator({
  id,
  isSelected,
  onToggle,
  style,
  shape,
  disabled = false,
  elementPath = 'checkout.form.orderBumps',
  varPrefix = BUMP_CONTROL_VAR_PREFIX,
}) {
  // Button, link and none show no control of their own, but the checkbox behind
  // them still has to exist: it is what the card's <label> toggles, and what a
  // keyboard reaches. The card carries the focus ring on its behalf.
  const isVisible = style === 'mark' || style === 'switch';

  return (
    <span
      className={clsx('relative inline-flex shrink-0', { 'size-0': !isVisible })}
      style={getControlVarOverrides(varPrefix)}
    >
      <input
        type="checkbox"
        id={id}
        name={id}
        checked={isSelected}
        onChange={onToggle}
        disabled={disabled}
        className={clsx(
          'absolute inset-0 size-full opacity-0',
          disabled ? 'cursor-default' : 'cursor-pointer',
        )}
      />
      {style === 'mark' && (
        <StyledElement
          as="span"
          elementPath={`${elementPath}.indicator`}
          checkEnabled={false}
          className={clsx(
            'cp_price-picker-option-indicator',
            `cp_price-picker-option-indicator--shape-${shape}`,
            'cp_order-bump-indicator',
            { 'cp_price-picker-option-indicator--checked': isSelected },
          )}
          aria-hidden="true"
        >
          {/* Tick and star are SVG icons, as a price option draws them — the
              dot is the ::after box and needs no child. */}
          {isSelected && shape === 'tick' && (
            <CheckIcon className="cp_price-picker-option-indicator-icon" />
          )}
          {isSelected && shape === 'star' && (
            <StarIcon className="cp_price-picker-option-indicator-icon" />
          )}
        </StyledElement>
      )}
      {style === 'switch' && (
        <StyledElement
          as="span"
          elementPath={`${elementPath}.switch`}
          checkEnabled={false}
          // The knob's inset, the room it leaves and the distance it travels are
          // all cut from the switch's own size, so the gap around it is even at
          // either end. The knob carries a shadow: it is the card's colour on a
          // track barely darker, and without one the off state reads as empty.
          className={clsx(
            'cp_order-bump-switch relative inline-block shrink-0 self-start rounded-full',
            'w-[calc(var(--cp_checkout-form-orderBumps-switch-size)*1.75)] h-[var(--cp_checkout-form-orderBumps-switch-size)]',
            '[transition:background-color_0.15s_ease]',
            "after:absolute after:top-1/2 after:rounded-full after:content-['']",
            'after:left-[calc(var(--cp_checkout-form-orderBumps-switch-size)*0.125)]',
            'after:size-[calc(var(--cp_checkout-form-orderBumps-switch-size)*0.75)]',
            'after:bg-[var(--cp_checkout-form-orderBumps-switch-color,var(--cp_price-picker-option-indicator-background-color))]',
            'after:[box-shadow:0_1px_2px_rgb(0_0_0/0.2)] after:[transition:translate_0.15s_ease]',
            isSelected
              ? 'cp_order-bump-switch--checked bg-[var(--cp_checkout-form-orderBumps-switch-background-color-selected)] after:[translate:calc(var(--cp_checkout-form-orderBumps-switch-size)*0.75)_-50%]'
              : 'bg-[var(--cp_checkout-form-orderBumps-switch-background-color,var(--cp_price-picker-option-indicator-border-color))] after:[translate:0_-50%]',
          )}
          aria-hidden="true"
        />
      )}
    </span>
  );
}

function BumpBadge({ text, icon }) {
  const hasIcon = isBadgeIcon(icon);
  if (!text && !hasIcon) return null;

  return (
    <StyledElement
      as="span"
      elementPath="checkout.form.orderBumps.badge"
      // Its row aligns it (see checkout.css): the wrapped line inverts the
      // cross axis, so a self-start here would put the badge at the BOTTOM.
      className={clsx(
        'cp_price-picker-option-badge cp_order-bump-badge min-w-0 max-w-full break-words whitespace-normal',
        { 'cp_price-picker-option-badge--with-icon': hasIcon },
      )}
    >
      {hasIcon && (
        <BadgeIcon
          icon={icon}
          className="flex-none text-[var(--cp_checkout-form-orderBumps-badge-icon-color)] w-[var(--cp_checkout-form-orderBumps-badge-icon-size)] h-[var(--cp_checkout-form-orderBumps-badge-icon-size)]"
        />
      )}
      {text}
    </StyledElement>
  );
}

// Each line of copy on a card dresses itself from its own element, so one can
// be set apart from the rest rather than inheriting whatever the card was given.
// Spelled out in full: Tailwind only generates classes it finds written whole.
const TYPOGRAPHY = {
  title: clsx(
    'text-[var(--cp_checkout-form-orderBumps-title-color)]',
    '[font-family:var(--cp_checkout-form-orderBumps-title-font-family)]',
    '[font-style:var(--cp_checkout-form-orderBumps-title-font-style)]',
    '[font-weight:var(--cp_checkout-form-orderBumps-title-font-weight)]',
    '[font-size:var(--cp_checkout-form-orderBumps-title-font-size)]',
    '[line-height:var(--cp_checkout-form-orderBumps-title-line-height)]',
  ),
  shortDescription: clsx(
    'text-[var(--cp_checkout-form-orderBumps-shortDescription-color)]',
    '[font-family:var(--cp_checkout-form-orderBumps-shortDescription-font-family)]',
    '[font-style:var(--cp_checkout-form-orderBumps-shortDescription-font-style)]',
    '[font-weight:var(--cp_checkout-form-orderBumps-shortDescription-font-weight)]',
    '[font-size:var(--cp_checkout-form-orderBumps-shortDescription-font-size)]',
    '[line-height:var(--cp_checkout-form-orderBumps-shortDescription-line-height)]',
  ),
  price: clsx(
    'text-[var(--cp_checkout-form-orderBumps-price-color)]',
    '[font-family:var(--cp_checkout-form-orderBumps-price-font-family)]',
    '[font-style:var(--cp_checkout-form-orderBumps-price-font-style)]',
    '[font-weight:var(--cp_checkout-form-orderBumps-price-font-weight)]',
    '[font-size:var(--cp_checkout-form-orderBumps-price-font-size)]',
    '[line-height:var(--cp_checkout-form-orderBumps-price-line-height)]',
  ),
  // A button's text colour is its own, so BumpCta sets this one.
  callToAction: clsx(
    '[font-family:var(--cp_checkout-form-orderBumps-callToAction-font-family)]',
    '[font-style:var(--cp_checkout-form-orderBumps-callToAction-font-style)]',
    '[font-weight:var(--cp_checkout-form-orderBumps-callToAction-font-weight)]',
    '[font-size:var(--cp_checkout-form-orderBumps-callToAction-font-size)]',
    '[line-height:var(--cp_checkout-form-orderBumps-callToAction-line-height)]',
  ),
  // The link's colour depends on the state, so BumpCta sets it.
  link: clsx(
    '[font-family:var(--cp_checkout-form-orderBumps-link-font-family)]',
    '[font-style:var(--cp_checkout-form-orderBumps-link-font-style)]',
    '[font-weight:var(--cp_checkout-form-orderBumps-link-font-weight)]',
    '[font-size:var(--cp_checkout-form-orderBumps-link-font-size)]',
    '[line-height:var(--cp_checkout-form-orderBumps-link-line-height)]',
  ),
  heading: clsx(
    'text-[var(--cp_checkout-form-orderBumps-heading-color)]',
    '[font-family:var(--cp_checkout-form-orderBumps-heading-font-family)]',
    '[font-style:var(--cp_checkout-form-orderBumps-heading-font-style)]',
    '[font-weight:var(--cp_checkout-form-orderBumps-heading-font-weight)]',
    '[font-size:var(--cp_checkout-form-orderBumps-heading-font-size)]',
    '[line-height:var(--cp_checkout-form-orderBumps-heading-line-height)]',
  ),
  features: clsx(
    'text-[var(--cp_checkout-form-orderBumps-features-color)]',
    '[font-family:var(--cp_checkout-form-orderBumps-features-font-family)]',
    '[font-style:var(--cp_checkout-form-orderBumps-features-font-style)]',
    '[font-weight:var(--cp_checkout-form-orderBumps-features-font-weight)]',
    '[font-size:var(--cp_checkout-form-orderBumps-features-font-size)]',
    '[line-height:var(--cp_checkout-form-orderBumps-features-line-height)]',
  ),
  callout: clsx(
    'text-[var(--cp_checkout-form-orderBumps-callout-color)]',
    '[font-family:var(--cp_checkout-form-orderBumps-callout-font-family)]',
    '[font-style:var(--cp_checkout-form-orderBumps-callout-font-style)]',
    '[font-weight:var(--cp_checkout-form-orderBumps-callout-font-weight)]',
    '[font-size:var(--cp_checkout-form-orderBumps-callout-font-size)]',
    '[line-height:var(--cp_checkout-form-orderBumps-callout-line-height)]',
  ),
};

// The accept button is the checkout's own button in miniature: untouched, it
// reads the submit button's colours, border and radius, so a seller who painted
// the pay button black gets a black accept button, and its text is the pay
// button's (derived by luminance in CheckoutCSS.js). Only a background the seller
// gives this button re-derives its text (readable-color, emitted only then).
// The primary-colour and white tails are for a stylesheet with no page behind
// it. A colour the seller set on this button sits first in each chain, so it
// holds under the pointer rather than jumping to the pay button's hover shade.
// Accepted, the button settles on that hover shade, as the pay button does
// under the pointer, and hovering it changes nothing more. The three colours
// resolve into local variables so the outline fill can reuse the border's.
const BUTTON_COLORS = {
  resting: clsx(
    '[--cp-order-bump-button-border:var(--cp_checkout-form-orderBumps-acceptButton-border-color,var(--cp_submit-button-border-color,var(--cp_color-primary)))]',
    '[--cp-order-bump-button-surface:var(--cp_checkout-form-orderBumps-acceptButton-background-color,var(--cp_submit-button-background-color,var(--cp_color-primary)))]',
    '[--cp-order-bump-button-text:var(--cp_checkout-form-orderBumps-acceptButton-color,var(--cp_checkout-form-orderBumps-acceptButton-readable-color,var(--cp_submit-button-text-color,var(--color-white))))]',
    '[&:hover]:[--cp-order-bump-button-border:var(--cp_checkout-form-orderBumps-acceptButton-border-color,var(--cp_submit-button-border-color-hover,var(--cp_color-primary-dark)))]',
    '[&:hover]:[--cp-order-bump-button-surface:var(--cp_checkout-form-orderBumps-acceptButton-background-color,var(--cp_submit-button-background-color-hover,var(--cp_color-primary-dark)))]',
    '[&:hover]:[--cp-order-bump-button-text:var(--cp_checkout-form-orderBumps-acceptButton-color,var(--cp_checkout-form-orderBumps-acceptButton-readable-color,var(--cp_submit-button-text-color-hover,var(--color-white))))]',
  ),
  // A selected colour the seller set sits first in each chain, then their
  // resting one. A solid button's text is re-derived against a selected
  // background (CheckoutCSS.js emits the selected readable colour only then).
  selected: clsx(
    '[--cp-order-bump-button-border:var(--cp_checkout-form-orderBumps-acceptButton-border-color-selected,var(--cp_checkout-form-orderBumps-acceptButton-border-color,var(--cp_submit-button-border-color-hover,var(--cp_color-primary-dark))))]',
    '[--cp-order-bump-button-surface:var(--cp_checkout-form-orderBumps-acceptButton-background-color-selected,var(--cp_checkout-form-orderBumps-acceptButton-background-color,var(--cp_submit-button-background-color-hover,var(--cp_color-primary-dark))))]',
    '[--cp-order-bump-button-text:var(--cp_checkout-form-orderBumps-acceptButton-color-selected,var(--cp_checkout-form-orderBumps-acceptButton-readable-color-selected,var(--cp_checkout-form-orderBumps-acceptButton-color,var(--cp_checkout-form-orderBumps-acceptButton-readable-color,var(--cp_submit-button-text-color-hover,var(--color-white))))))]',
  ),
};

// The Fill field's Outline: the button's colour drawn as the border, the text
// following it so the two never drift apart, and the surface the accept bar's so
// the card shows through. The seller's own Text and Background still win.
const BUTTON_FILLS = {
  solid: 'text-[var(--cp-order-bump-button-text)] bg-[var(--cp-order-bump-button-surface)]',
  outline:
    'text-[var(--cp_checkout-form-orderBumps-acceptButton-color,var(--cp-order-bump-button-border))] bg-[var(--cp_checkout-form-orderBumps-acceptButton-background-color,var(--cp_checkout-form-orderBumps-acceptBar-background-color))]',
  outlineSelected:
    'text-[var(--cp_checkout-form-orderBumps-acceptButton-color-selected,var(--cp_checkout-form-orderBumps-acceptButton-color,var(--cp-order-bump-button-border)))] bg-[var(--cp_checkout-form-orderBumps-acceptButton-background-color-selected,var(--cp_checkout-form-orderBumps-acceptButton-background-color,var(--cp_checkout-form-orderBumps-acceptBar-background-color)))]',
};

const FIT_BUTTON_SIDES = { left: 'self-start', right: 'self-end', center: 'self-center' };

/**
 * `placement` is where the layout put it: the corner, a line of its own, or
 * the top of the offer column.
 */
function BumpCta({ text, style, isSelected, width, fill, side, placement }) {
  if (!text) return null;

  // A button has no mark beside it to say which way it went, so it leads with
  // one of its own: a plus to add, a tick once added. The copy stays the same
  // either way — it is the seller's, and "Yes, add it" still reads next to a
  // tick. Decorative, so the checkbox behind the card keeps the state.
  const Icon = isSelected ? CheckIcon : PlusIconSolid;
  const isButton = style === 'button';
  const isFit = isButton && width === 'fit';
  const isOutline = isButton && fill === 'outline';

  let buttonSize = null;
  if (isButton) {
    // In the corner the button is the corner: nothing wider to fill, no side to
    // keep to. On a line the cross axis is vertical, so the line places a fit
    // button. In the offer column there is no line, so it keeps to its side.
    if (placement === 'corner') buttonSize = 'w-auto self-auto';
    else if (!isFit) buttonSize = 'w-full';
    else if (placement === 'line') buttonSize = 'w-auto self-auto';
    else buttonSize = clsx('w-auto', FIT_BUTTON_SIDES[side] ?? 'self-start');
  }

  let fillClass = BUTTON_FILLS.solid;
  if (isOutline) fillClass = isSelected ? BUTTON_FILLS.outlineSelected : BUTTON_FILLS.outline;

  return (
    <StyledElement
      as="span"
      elementPath="checkout.form.orderBumps.callToAction"
      // The width rides on the button itself rather than on the line it may
      // sit on, because beside the offer there is no line: the button is a
      // flex item of the offer column, and would stretch to fill it whatever
      // the seller asked for.
      className={clsx(
        'cp_order-bump-cta',
        style === 'link' ? TYPOGRAPHY.link : TYPOGRAPHY.callToAction,
        {
          'text-[var(--cp_checkout-form-orderBumps-callToAction-color)]':
            !isButton && style !== 'link',
          'cp_order-bump-cta--button': isButton,
          'cp_order-bump-cta--button-fit': isFit,
          [`cp_order-bump-cta--button-fit-${side}`]: isFit,
          'cp_order-bump-cta--button-outline': isOutline,
          'cp_order-bump-cta--link [text-decoration:var(--cp_checkout-form-orderBumps-link-text-decoration,underline)] underline-offset-2':
            style === 'link',
          // Accepted, the link darkens a shade, as the pay button does under
          // the pointer, unless the seller set the selected colour apart.
          'text-[var(--cp_checkout-form-orderBumps-link-color-selected,color-mix(in_srgb,var(--cp_checkout-form-orderBumps-link-color),black_10%))]':
            style === 'link' && isSelected,
          'text-[var(--cp_checkout-form-orderBumps-link-color)]': style === 'link' && !isSelected,
        },
        isButton && [
          'inline-flex items-center justify-center gap-[0.375em] text-center',
          isSelected ? BUTTON_COLORS.selected : BUTTON_COLORS.resting,
          fillClass,
          buttonSize,
          '[border:var(--cp_checkout-form-orderBumps-acceptButton-border-width,var(--cp_checkout-form-button-border-width,1.5px))_solid_var(--cp-order-bump-button-border)]',
          'rounded-[var(--cp_checkout-form-orderBumps-acceptButton-border-radius,var(--cp_checkout-form-button-border-radius,var(--cp_button-border-radius,4px)))]',
          '[padding:var(--cp_checkout-form-orderBumps-acceptButton-padding-y,0.375rem)_var(--cp_checkout-form-orderBumps-acceptButton-padding-x,0.75rem)]',
          '[transition:color_0.15s_ease,border-color_0.15s_ease,background-color_0.15s_ease]',
          // Opening the offer column, a button sits as far above the offer as
          // it does on a line of its own: the column's gap, topped up to the
          // card's padding.
          placement === 'offer' &&
            'mb-[max(0px,calc(var(--cp_price-picker-option-padding)_-_0.5rem))]',
        ],
      )}
    >
      {isButton && (
        <Icon
          className={clsx('cp_order-bump-cta-icon size-[1.125em] shrink-0', {
            // The tick settles in rather than snapping: the plus has just left,
            // and a change in the same spot reads as a flicker.
            'cp_order-bump-cta-icon--added motion-safe:animate-[cp-order-bump-tick-in_0.2s_ease-out]':
              isSelected,
          })}
          aria-hidden="true"
        />
      )}
      {text}
    </StyledElement>
  );
}

function BumpMedia({ offer, isPreview, size, ratio }) {
  // The gallery lives on the product; the card has room for one thumbnail.
  const image = offer.product?.media?.[0];
  const {
    modifier,
    requestWidth,
    className: sizeClassName,
  } = THUMBNAIL_SIZES[size] ?? THUMBNAIL_SIZES[DEFAULT_THUMBNAIL_SIZE];
  const {
    modifier: ratioModifier,
    heightFactor,
    className: ratioClassName,
  } = ASPECT_RATIOS[ratio] ?? ASPECT_RATIOS.square;
  const requestHeight = heightFactor ? Math.round(requestWidth * heightFactor) : undefined;
  const imageProps =
    image?.url &&
    getImageSrcAndSrcSet({
      url: image.url,
      width: requestWidth,
      height: requestHeight,
      isPreview,
    });

  if (!imageProps) return null;

  // Spanning the card, the picture is a banner: it runs out through the card's
  // padding to the border on three sides, with the padding put back beneath it,
  // and the card's own corners clip it, so the seller's image radius has
  // nothing to round. Nothing of its own to clip either: the picture fills the
  // box exactly. Its width is left to the flow, since a percentage would stop
  // a padding short of each edge.
  const isBanner = modifier === 'full';

  return (
    <StyledElement
      elementPath="checkout.form.orderBumps.image"
      className={clsx(
        'cp_order-bump-image-wrapper',
        `cp_order-bump-image-wrapper--${modifier}`,
        ratioModifier && `cp_order-bump-image-wrapper--${ratioModifier}`,
        ratioClassName,
        isBanner
          ? 'cp_order-bump-banner w-auto [margin:calc(var(--cp_price-picker-option-padding)*-1)_calc(var(--cp_price-picker-option-padding)*-1)_var(--cp_price-picker-option-padding)]'
          : // A thumbnail has to hold its box against a long title.
            clsx(
              sizeClassName,
              'shrink-0 overflow-hidden rounded-[var(--cp_checkout-form-orderBumps-image-border-radius)]',
            ),
      )}
    >
      <img
        {...imageProps}
        alt={image.name || offer.title}
        width={requestWidth}
        // Only when the box is known — the picture's own height is whatever it
        // was uploaded at, and a guess here would be a wrong aspect box.
        height={requestHeight}
        loading="lazy"
        className={clsx(
          'cp_order-bump-image w-full',
          // With no box to fill there is nothing to crop to, so the picture
          // sets its own height.
          ratioModifier ? 'h-full object-cover' : 'h-auto',
        )}
      />
    </StyledElement>
  );
}

const soldOutBadgeClasses = clsx(
  'cp_sold-out cp_order-bump-soldout my-1 w-fit self-auto! whitespace-nowrap',
  'text-[var(--cp_checkout-statusBadges-soldOut-color)]',
  'bg-[var(--cp_checkout-statusBadges-soldOut-background-color)]',
  '[font-family:var(--cp_checkout-statusBadges-font-family)]',
  '[font-style:var(--cp_checkout-statusBadges-font-style)]',
  '[font-size:var(--cp_checkout-statusBadges-font-size)]',
  '[line-height:var(--cp_checkout-statusBadges-line-height)]',
  '[font-weight:var(--cp_checkout-statusBadges-font-weight)]',
);

function BumpTitleBlock({ offer, soldOutText }) {
  return (
    <>
      <StyledElement
        as="span"
        elementPath="checkout.form.orderBumps.title"
        className={clsx('cp_order-bump-title', TYPOGRAPHY.title)}
      >
        {offer.title}
      </StyledElement>
      {/* Badge centres itself; this one follows the title block's alignment. */}
      {soldOutText && <Badge className={soldOutBadgeClasses}>{soldOutText}</Badge>}
      {offer.shortDescription && (
        <StyledElement
          as="span"
          elementPath="checkout.form.orderBumps.shortDescription"
          className={clsx('cp_order-bump-short-description', TYPOGRAPHY.shortDescription)}
        >
          {offer.shortDescription}
        </StyledElement>
      )}
    </>
  );
}

/**
 * The seller's line above the card ("Special one time offer"). Outside the
 * card on purpose: it is not part of the label, so clicking it does not take
 * the bump, and the card beneath keeps the arrangement its neighbours have.
 * Padding rather than margin so the eye that hides it takes its space with it.
 */
function BumpCardHeading({ text, isCentered }) {
  if (!text) return null;

  return (
    <StyledElement
      elementPath="checkout.form.orderBumps.heading"
      className={clsx(
        'cp_order-bump-heading pb-[var(--cp_checkout-form-orderBumps-heading-padding-bottom)]',
        TYPOGRAPHY.heading,
        { 'text-center': isCentered },
      )}
    >
      {text}
    </StyledElement>
  );
}

function BumpPrice({ priceText, intervalText, discountedFromText, setupFeeText }) {
  return (
    <>
      <StyledElement
        as="span"
        elementPath="checkout.form.orderBumps.price"
        className={clsx('cp_order-bump-price', TYPOGRAPHY.price)}
      >
        {/* Struck-through original ahead of the price, as a price option shows it. */}
        {discountedFromText && (
          <StyledElement
            as="span"
            elementPath="checkout.form.orderBumps.discountedFromPrice"
            className="cp_price-picker-option-discounted-from cp_order-bump-discounted-from mr-1"
          >
            {discountedFromText}
          </StyledElement>
        )}
        {priceText}
        {intervalText ? ` ${intervalText}` : ''}
      </StyledElement>

      {setupFeeText && (
        <StyledElement
          as="div"
          elementPath="checkout.form.orderBumps.setupFee"
          className="cp_price-picker-option-setup-fee cp_order-bump-setup-fee"
        >
          {setupFeeText}
        </StyledElement>
      )}
    </>
  );
}

/**
 * The same stepper a quantity variant option carries, on the placements that
 * opted into one — so the two controls on a checkout are one control.
 *
 * It stands in the selection mark's own place, top right, and REPLACES it: the
 * count already says whether the bump is taken, so a circle beside it would be a
 * second control saying the same thing. Stepping to 0 drops the bump and
 * stepping up takes it, which is why it is on screen from the start.
 */
function BumpQuantity({ offer, quantity, max, onChange }) {
  return (
    // The card behind it adds one on click; without this, pressing + would add
    // two and pressing − would net zero.
    <div
      className="cp_order-bump-quantity flex shrink-0 items-stretch"
      onClick={event => event.stopPropagation()}
    >
      <QuantityButton
        className="cp_order-bump-quantity-button-minus"
        onClick={() => onChange(Math.max(quantity - 1, 0))}
      >
        <MinusIcon className="cp_order-bump-quantity-button-icon w-5 h-5" />
      </QuantityButton>
      <QuantityField
        className="cp_order-bump-quantity-input"
        id={`order-bump-quantity-${offer.productId}`}
        name={`order-bump-quantity-${offer.productId}`}
        value={quantity}
        onChange={value => onChange(value === '' ? 0 : Math.min(Math.max(value, 0), max))}
        required={false}
        min={0}
        max={max}
      />
      <QuantityButton
        className="cp_order-bump-quantity-button-plus"
        onClick={() => onChange(Math.min(quantity + 1, max))}
      >
        <PlusIcon className="cp_order-bump-quantity-button-icon w-5 h-5" />
      </QuantityButton>
    </div>
  );
}

/**
 * The "what you get" list, between the price and the description. It rides the
 * description's slot so it follows the description's width setting, and the
 * seller's Show more toggle cuts the description alone: the list is the part
 * meant to be read at a glance.
 *
 * A grid runs two across where the card can give each column 7rem, which the
 * text column beside a thumbnail can at desktop width, and one where it cannot,
 * so a narrow grid cell never squeezes a line to a word. The 40% floor keeps a
 * wide list at two columns rather than three.
 */
function BumpFeatures({ features, layout, isCentered }) {
  if (!features?.length) return null;

  return (
    <StyledElement
      as="ul"
      elementPath="checkout.form.orderBumps.features"
      className={clsx(
        'cp_order-bump-features',
        `cp_order-bump-features--${layout}`,
        'mt-2 list-none p-0 gap-[var(--cp_checkout-form-orderBumps-features-gap)]',
        TYPOGRAPHY.features,
        layout === 'grid'
          ? 'grid grid-cols-[repeat(auto-fit,minmax(max(7rem,40%),1fr))]'
          : 'flex flex-col',
        { 'cp_order-bump-features--centered items-center justify-items-center': isCentered },
      )}
    >
      {features.map((feature, index) => (
        <li key={index} className="cp_order-bump-feature flex min-w-0 items-start gap-[0.5em]">
          {feature.icon !== NO_FEATURE_ICON && (
            <BadgeIcon
              icon={isBadgeIcon(feature.icon) ? feature.icon : DEFAULT_FEATURE_ICON}
              // Centred on the first line of text.
              className="flex-none text-[var(--cp_checkout-form-orderBumps-features-icon-color)] w-[var(--cp_checkout-form-orderBumps-features-icon-size)] h-[var(--cp_checkout-form-orderBumps-features-icon-size)] mt-[calc((1lh_-_var(--cp_checkout-form-orderBumps-features-icon-size))/2)]"
            />
          )}
          <span className="cp_order-bump-feature-text min-w-0">{feature.text}</span>
        </li>
      ))}
    </StyledElement>
  );
}

/**
 * The call out box under the description: what the seller wants read before
 * the customer decides, in a box of its own with an optional icon. The one
 * place on the card the seller types HTML by hand, so it is cleaned again here
 * on top of the API's own pass on save. Its background is a wash of its own
 * text colour, so it reads in either mode, and a link in it is dressed as one
 * in the description is.
 */
function BumpCallout({ text, icon, isCentered }) {
  const html = useMemo(() => sanitizeCalloutHtml(text), [text]);
  if (!html) return null;

  return (
    <StyledElement
      elementPath="checkout.form.orderBumps.callout"
      className={clsx(
        'cp_order-bump-callout flex items-start gap-[0.5em] mt-3',
        'p-[var(--cp_checkout-form-orderBumps-callout-padding)]',
        'bg-[var(--cp_checkout-form-orderBumps-callout-background-color)]',
        '[border:var(--cp_checkout-form-orderBumps-callout-border-width)_solid_var(--cp_checkout-form-orderBumps-callout-border-color)]',
        'rounded-[var(--cp_checkout-form-orderBumps-callout-border-radius)]',
        TYPOGRAPHY.callout,
        isCentered ? 'cp_order-bump-callout--centered justify-center text-center' : 'text-left',
      )}
      // The card takes a click anywhere on it; a link's is the link's own.
      onClick={event => {
        if (event.target.closest('a')) event.stopPropagation();
      }}
    >
      {isBadgeIcon(icon) && (
        <BadgeIcon
          icon={icon}
          className="flex-none text-[var(--cp_checkout-form-orderBumps-callout-icon-color)] w-[var(--cp_checkout-form-orderBumps-callout-icon-size)] h-[var(--cp_checkout-form-orderBumps-callout-icon-size)] mt-[calc((1lh_-_var(--cp_checkout-form-orderBumps-callout-icon-size))/2)]"
        />
      )}
      <MultilineText
        className="cp_order-bump-callout-text min-w-0 [&_a]:text-[var(--cp_description-link-text-color)] [&_a]:[font-weight:calc(var(--cp_font-body-weight)_+_100)] [&_a]:underline"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </StyledElement>
  );
}

// One line, in the description's own font size rather than a pixel figure, so a
// theme that resizes the text still cuts at a line boundary.
const SHORTENED_DESCRIPTION_MAX_HEIGHT = '1.5em';

/**
 * The description, cut to its first line behind a toggle on the placements that
 * asked for one.
 *
 * Cut with `-webkit-line-clamp`, which counts lines through the nested
 * paragraphs of rich HTML and ends the visible one with an ellipsis even when
 * the first paragraph is short and the rest is what got hidden. The max-height
 * stays as a backstop: a leading image is a line the clamp lets through.
 *
 * The toggle is rendered whenever the seller ticked the box, without measuring
 * whether the text actually overflows. Measuring means reading layout after
 * paint and re-rendering on resize and webfont swap; the seller opted in per
 * placement, so the check would earn very little.
 *
 * Shortened, the toggle shares the teaser's line — a link on a line of its own
 * gives back the line the cut saved. The teaser breaks mid-word so it fills the
 * line and the ellipsis lands against the toggle; cut at a word boundary, the
 * gap between them was as wide as whatever word didn't fit. The teaser asks
 * for 8rem beside the toggle, and where a narrow grid cell cannot give it that
 * the row wraps and the toggle drops under, rather than leaving a word of
 * teaser next to the link.
 * Expanded, it sits under the full text, where a run of block paragraphs
 * leaves it nowhere inline to sit.
 */
// A word wider than the column (a long product name in a narrow grid cell)
// would otherwise run out past the card's edge. Clipped on the axis it spills
// on only, so the vertical axis stays open for the toggle's focus ring. The
// rich-text blocks colour themselves from the product description's variables;
// inside a bump every line of copy is the Cards description instead, links
// aside, which keep the link colour.
const COPY_COLUMN = clsx(
  'overflow-x-clip',
  '[--cp_description-body-text-color:var(--cp_card-description-color)]',
  '[--cp_description-heading-text-color:var(--cp_card-description-color)]',
  '[--cp_description-quote-text-color:var(--cp_card-description-color)]',
  '[--cp_description-bullet-color:var(--cp_card-description-color)]',
  '[--cp_description-collapsibles-title-text-color:var(--cp_card-description-color)]',
  '[--cp_description-collapsibles-body-text-color:var(--cp_card-description-color)]',
);

function BumpBody({ id, html, isShortenable, isCentered, labels }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!html) return null;

  const isShortened = isShortenable && !isExpanded;

  return (
    <div
      className={clsx(
        'cp_order-bump-description mt-2 text-[var(--cp_card-description-color)]',
        COPY_COLUMN,
        {
          // Carries its own centring rather than the text column's, so it follows
          // the alignment whether it sits in the column or runs the card's width.
          'cp_order-bump-description--centered text-center': isCentered,
          'flex flex-wrap items-baseline gap-x-2 gap-y-1': isShortened,
          'justify-center': isShortened && isCentered,
        },
      )}
    >
      <div
        id={id}
        className={clsx('cp_rich-text', {
          'cp_order-bump-description--shortened min-w-0 grow shrink basis-32 overflow-hidden':
            isShortened,
        })}
        style={
          isShortened
            ? {
                maxHeight: SHORTENED_DESCRIPTION_MAX_HEIGHT,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
              }
            : undefined
        }
      >
        <MultilineText
          // Important because MultilineText sets its own word-break inline.
          className={clsx({ 'break-all!': isShortened })}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>

      {isShortenable && (
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-controls={id}
          // The card is a <label> (or, with a stepper, a click target that adds
          // one), so a bare click here would take the bump as well as expand it.
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            setIsExpanded(current => !current);
          }}
          className={clsx(
            'cp_order-bump-description-toggle cursor-pointer font-semibold underline text-[var(--cp_checkout-form-orderBumps-callToAction-color)]',
            isShortened ? 'shrink-0 whitespace-nowrap' : 'mt-1',
          )}
        >
          {isExpanded ? labels.orderBumpShowLessText : labels.orderBumpShowMoreText}
        </button>
      )}
    </div>
  );
}

const ACCEPT_STYLES = ['mark', 'switch', 'button', 'link', 'none'];
// What an untouched theme shows, and what a style with nothing to render falls
// back to.
const DEFAULT_ACCEPT_STYLE = 'switch';
// What the call to action sits on: a band flush with the card's sides, a boxed
// row of its own, or nothing but the offer.
const ACCEPT_APPEARANCES = ['bar', 'row', 'offer'];
const DEFAULT_ACCEPT_APPEARANCE = 'offer';
// The position is one value in the theme — `top-right` — because a seller
// picks a corner, not an edge and then a side. The middle edge is a side of
// the offer, centred on the row, so it has no centre of its own.
const ACCEPT_EDGES = ['top', 'center', 'bottom'];
const ACCEPT_SIDES = ['right', 'left', 'center'];
const DEFAULT_ACCEPT_POSITION = { edge: 'top', side: 'right' };
const MEDIA_POSITIONS = ['left', 'right', 'top', 'none'];
export const MARK_SHAPES = ['dot', 'tick', 'star'];

/**
 * The seller's arrangement settings. An untouched theme has none of them set
 * and renders the Switch preset, the first chip in the design panel: a bare
 * switch at the top right of the offer. The fallbacks here and the registry's
 * defaults have to agree, or the chip the panel lights would not be the card.
 */
function resolveThumbnailSize(value) {
  if (value === 'full') return 'full';
  const size = Number(value);
  return THUMBNAIL_SIZES[size] ? size : DEFAULT_THUMBNAIL_SIZE;
}

// Square is the fallback, at every size: cards in a row line up, and a ratio
// always means what it says rather than depending on the size beside it.
function resolveAspectRatio(value) {
  return ASPECT_RATIOS[value] ? value : 'square';
}

export function getOrderBumpLayout(theme) {
  const settings = theme?.elements?.checkout?.form?.orderBumps || {};
  const grid = /^grid-(\d+)$/.exec(typeof settings.layout === 'string' ? settings.layout : '');
  return {
    columns: grid ? Math.min(Math.max(parseInt(grid[1], 10), 2), 3) : 1,
    acceptStyle: ACCEPT_STYLES.includes(settings.acceptStyle)
      ? settings.acceptStyle
      : DEFAULT_ACCEPT_STYLE,
    acceptAppearance: ACCEPT_APPEARANCES.includes(settings.acceptAppearance)
      ? settings.acceptAppearance
      : DEFAULT_ACCEPT_APPEARANCE,
    ...resolveAcceptPosition(settings.acceptPosition),
    markShape: MARK_SHAPES.includes(settings.indicator?.styles?.shape)
      ? settings.indicator.styles.shape
      : 'dot',
    mediaPosition: MEDIA_POSITIONS.includes(settings.mediaPosition)
      ? settings.mediaPosition
      : 'left',
    align: settings.horizontalAlign === 'center' ? 'center' : 'left',
    buttonWidth: settings.acceptButton?.width === 'full' ? 'full' : 'fit',
    buttonFill: settings.acceptButton?.fill === 'outline' ? 'outline' : 'solid',
    descriptionPosition: settings.descriptionPosition === 'full' ? 'full' : 'column',
    featuresLayout: settings.features?.layout === 'grid' ? 'grid' : 'list',
    // Null, not a default: unset keeps the rule the card had before the field
    // existed, which depends on what else is on the card — see the layout.
    badgePosition: ['above', 'inline'].includes(settings.badge?.position)
      ? settings.badge.position
      : null,
    // The box itself is CSS; these only pick which one.
    mediaSize: resolveThumbnailSize(settings.image?.styles?.size),
    mediaRatio: resolveAspectRatio(settings.image?.styles?.aspectRatio),
  };
}

function resolveAcceptPosition(value) {
  const [edge, side] = typeof value === 'string' ? value.split('-') : [];
  const isKnown =
    ACCEPT_EDGES.includes(edge) &&
    ACCEPT_SIDES.includes(side) &&
    !(edge === 'center' && side === 'center');
  return {
    acceptEdge: isKnown ? edge : DEFAULT_ACCEPT_POSITION.edge,
    acceptSide: isKnown ? side : DEFAULT_ACCEPT_POSITION.side,
  };
}

/**
 * The card's arrangement, from the pieces above.
 *
 * One component driven by the settings rather than one per preset: the settings
 * are orthogonal, so a seller who picks the banner arrangement and then moves
 * the image to the right lands on a combination no fixed layout would cover.
 * The default — accept copy leading the checkbox row, thumbnail and heading
 * stacked beside it like a variant option, description closing the card
 * full-width — is what every value falls back to.
 */
// Beside a fit button or link the description drops under the row and keeps
// the thumbnail's indent. The indent has to go where the copy goes: the media
// row drops the copy under the picture once the picture, its gap and the copy's
// 8rem will not fit across. The offer asks for that same sum, so the corner
// wraps onto a line of its own before the copy can, leaving the row the
// header's full width; the header is a container, so the description, outside
// the row, watches the width the row wraps on.
const BODY_INDENTS = {
  sm: {
    offer: 'basis-[calc(64px_+_9rem)]',
    body: String.raw`[&>.cp\_order-bump-description]:ml-[calc(64px_+_1rem)] @max-[calc(64px_+_9rem)]:[&>.cp\_order-bump-description]:ml-0 [&>.cp\_order-bump-amount]:ml-[calc(64px_+_1rem)] @max-[calc(64px_+_9rem)]:[&>.cp\_order-bump-amount]:ml-0`,
  },
  md: {
    offer: 'basis-[calc(128px_+_9rem)]',
    body: String.raw`[&>.cp\_order-bump-description]:ml-[calc(128px_+_1rem)] @max-[calc(128px_+_9rem)]:[&>.cp\_order-bump-description]:ml-0 [&>.cp\_order-bump-amount]:ml-[calc(128px_+_1rem)] @max-[calc(128px_+_9rem)]:[&>.cp\_order-bump-amount]:ml-0`,
  },
  lg: {
    offer: 'basis-[calc(192px_+_9rem)]',
    body: String.raw`[&>.cp\_order-bump-description]:ml-[calc(192px_+_1rem)] @max-[calc(192px_+_9rem)]:[&>.cp\_order-bump-description]:ml-0 [&>.cp\_order-bump-amount]:ml-[calc(192px_+_1rem)] @max-[calc(192px_+_9rem)]:[&>.cp\_order-bump-amount]:ml-0`,
  },
};

// The call to action's own line. The side facing the offer is whichever of the
// box border and the divider is thicker, so a bar carries a hairline without
// being boxed in, with no second node and no second colour to keep in step.
// Taken, the surface and its edge change colour with the card, and an edge
// gains a 1px inset ring as the card's does: a shadow rather than a wider rule,
// so nothing moves when the bump is toggled.
const ACCEPT_LINE_SURFACE = {
  resting:
    'bg-[var(--cp_checkout-form-orderBumps-acceptBar-background-color)] [border-color:var(--cp_checkout-form-orderBumps-acceptBar-border-color)] [border-style:var(--cp_checkout-form-orderBumps-border-style,solid)]',
  selected:
    'bg-[var(--cp_checkout-form-orderBumps-acceptBar-background-color-selected)] [border-color:var(--cp_checkout-form-orderBumps-acceptBar-border-color-selected)] [border-style:var(--cp_checkout-form-orderBumps-border-style,solid)]',
};
const ACCEPT_LINE_RING = {
  all: '[box-shadow:inset_0_0_0_1px_var(--cp_checkout-form-orderBumps-acceptBar-border-color-selected)]',
  // Under a banner the card wears its ring outside the border, so there is no
  // inside ring for a bar to redraw: only the divider thickens.
  top: '[box-shadow:inset_0_-1px_0_0_var(--cp_checkout-form-orderBumps-acceptBar-border-color-selected)]',
  bottom:
    '[box-shadow:inset_0_1px_0_0_var(--cp_checkout-form-orderBumps-acceptBar-border-color-selected)]',
};

function acceptLineClassName({
  appearance,
  edge,
  side,
  isButton,
  hasSelector,
  isSelected,
  isBanner,
}) {
  const isTop = edge === 'top';
  let sideClass = null;
  if (side === 'right')
    sideClass = hasSelector ? 'flex-row-reverse justify-between' : 'justify-end';
  else if (side === 'center') sideClass = 'justify-center';

  let appearanceClass;
  if (appearance === 'offer') {
    // Bare copy sits as close to the offer as the copy in the offer column
    // does. A button is a control, not copy: it keeps the card's padding
    // between it and the offer, as a row does.
    if (isButton) {
      appearanceClass = isTop
        ? 'mb-[var(--cp_price-picker-option-padding)]'
        : 'mt-[var(--cp_price-picker-option-padding)]';
    } else {
      appearanceClass = isTop ? 'mb-2' : 'mt-2';
    }
  } else if (appearance === 'row') {
    // A row's box is as heavy as the card's edge unless the row sets its own.
    appearanceClass = clsx(
      'p-[var(--cp_checkout-form-orderBumps-acceptBar-padding,12px)]',
      'rounded-[var(--cp_checkout-form-orderBumps-acceptBar-border-radius,var(--cp_button-border-radius,4px))]',
      isSelected ? ACCEPT_LINE_SURFACE.selected : ACCEPT_LINE_SURFACE.resting,
      isTop
        ? 'mb-[var(--cp_price-picker-option-padding)] [border-width:var(--cp_checkout-form-orderBumps-acceptBar-border-width,var(--cp_checkout-form-orderBumps-border-width,1px))_var(--cp_checkout-form-orderBumps-acceptBar-border-width,var(--cp_checkout-form-orderBumps-border-width,1px))_max(var(--cp_checkout-form-orderBumps-acceptBar-border-width,var(--cp_checkout-form-orderBumps-border-width,1px)),var(--cp_checkout-form-orderBumps-acceptBar-divider-width,0px))_var(--cp_checkout-form-orderBumps-acceptBar-border-width,var(--cp_checkout-form-orderBumps-border-width,1px))]'
        : 'mt-[var(--cp_price-picker-option-padding)] [border-width:max(var(--cp_checkout-form-orderBumps-acceptBar-border-width,var(--cp_checkout-form-orderBumps-border-width,1px)),var(--cp_checkout-form-orderBumps-acceptBar-divider-width,0px))_var(--cp_checkout-form-orderBumps-acceptBar-border-width,var(--cp_checkout-form-orderBumps-border-width,1px))_var(--cp_checkout-form-orderBumps-acceptBar-border-width,var(--cp_checkout-form-orderBumps-border-width,1px))_var(--cp_checkout-form-orderBumps-acceptBar-border-width,var(--cp_checkout-form-orderBumps-border-width,1px))]',
      isSelected && ACCEPT_LINE_RING.all,
    );
  } else {
    // A bar runs flush with the card's sides: pulled out through the card's
    // padding, with that padding put back inside it so its copy lines up with
    // the offer. No box, since its sides would land on the card's own, and a
    // hairline between it and the offer as heavy as the card's edge unless the
    // bar sets its own divider.
    let ring = null;
    if (isSelected)
      ring = isBanner ? ACCEPT_LINE_RING[isTop ? 'top' : 'bottom'] : ACCEPT_LINE_RING.all;
    appearanceClass = clsx(
      'px-[var(--cp_price-picker-option-padding)] py-[var(--cp_checkout-form-orderBumps-acceptBar-padding,12px)]',
      'mx-[calc(var(--cp_price-picker-option-padding)*-1)]',
      isSelected ? ACCEPT_LINE_SURFACE.selected : ACCEPT_LINE_SURFACE.resting,
      isTop
        ? 'mt-[calc(var(--cp_price-picker-option-padding)*-1)] mb-[var(--cp_price-picker-option-padding)] [border-width:0_0_var(--cp_checkout-form-orderBumps-acceptBar-divider-width,var(--cp_checkout-form-orderBumps-border-width,1px))_0]'
        : 'mt-[var(--cp_price-picker-option-padding)] mb-[calc(var(--cp_price-picker-option-padding)*-1)] [border-width:var(--cp_checkout-form-orderBumps-acceptBar-divider-width,var(--cp_checkout-form-orderBumps-border-width,1px))_0_0_0]',
      ring,
    );
  }

  // Nothing visible to sit beside, no gap.
  return clsx('flex items-center', hasSelector ? 'gap-3' : 'gap-0', sideClass, appearanceClass);
}

function OrderBumpCardLayout({
  indicator,
  cta,
  media,
  badge,
  titleBlock,
  price,
  body,
  amountInput,
  acceptAppearance,
  acceptEdge,
  acceptSide,
  acceptStyle,
  mediaPosition,
  align,
  descriptionPosition,
  badgePosition,
  buttonWidth,
  mediaSize,
  hasMedia,
  isQuantity,
  hasCta,
  isSelected,
  hasBanner,
}) {
  const isCentered = align === 'center';
  // A banner is the card's, not the offer's: it leads everything, the call to
  // action's own line included, and leaves the offer column to the copy.
  const isBanner = mediaPosition === 'banner';
  const isMediaRow = !['none', 'top', 'banner'].includes(mediaPosition);
  // None is no call to action at all: no control, no copy, and no line or
  // corner for either to sit in. The card's own highlight says it was taken.
  const showsCta = acceptStyle !== 'none';
  // Beside the offer, the control takes a column beside it, whatever the
  // selector: a mark or switch with its copy opening the offer's own column,
  // or a button or link that IS the copy. At the top right it sits in the
  // corner; on the middle edge it centres on the row, on either side. Everywhere
  // else the two get a line of their own: a bare one that opens or closes the
  // card, or a row or bar. The top LEFT is a line too, not a mirrored corner: a
  // column on the left indents all of the offer past the control, so copy
  // under the call to action reads as dropped beneath it rather than beside
  // the offer. Centred on the row there is no copy under it to drop, which is
  // why the middle edge gets a left column and the top does not.
  const hasLine =
    showsCta &&
    (acceptAppearance !== 'offer' ||
      acceptEdge === 'bottom' ||
      (acceptEdge === 'top' && acceptSide === 'left'));
  const isCornerCentered = !hasLine && acceptEdge === 'center';
  const isCornerLeft = !hasLine && acceptSide === 'left';

  // Two things keep a button or link in the column above the offer instead of
  // the corner: a full button, which a corner cannot hold, and a centred card,
  // which lifts its corner out of the flow to a mark's width.
  const isCtaInCorner =
    !hasLine &&
    showsCta &&
    !isCentered &&
    (acceptStyle === 'link' || (acceptStyle === 'button' && buttonWidth === 'fit'));

  // A row carries the call to action in a box, which is what makes it read as
  // the pitch rather than as copy. A button sits inside it like any control.
  const isBoxed = acceptAppearance === 'row';

  // Button, link and none leave a zero-width checkbox where the control would
  // be: real enough for a flex gap to indent the copy past it, or to shift a
  // centred button off centre by half of one. A stepper is a real control,
  // whatever the style. (See BumpIndicator for which styles show a control.)
  const hasSelector = isQuantity || ['mark', 'switch'].includes(acceptStyle);
  const hasCornerContent = hasSelector || isCtaInCorner;

  // Where the highlight rides. Set, it is the seller's. Unset, it takes the
  // title's row only when nothing opens the card ahead of it: beside a call to
  // action that leads the card it floated between the pitch and the mark with
  // nothing to line up with. A centred card never shares the row, as a centred
  // price option stacks it.
  const isBadgeAbove =
    isCentered || (badgePosition ? badgePosition === 'above' : hasCta && showsCta);

  // Beside a fit button or link the text column is what is left between the
  // thumbnail and the corner — a third of the card, on a wide one. So the
  // description leaves the column for a line under the row, from the title's
  // left edge out under the button to the card's right.
  // Only there: a mark or switch is too narrow to squeeze anything, and a
  // corner on the LEFT indents the offer past the control as well, which the
  // indent under the row could not follow.
  const dropsBody = descriptionPosition === 'column' && isCtaInCorner && !isCornerLeft;
  // The indent is the thumbnail's, so the class carries which thumbnail —
  // and none where no picture is on the copy's left to clear.
  const bodyIndent =
    dropsBody && hasMedia && mediaPosition === 'left' ? THUMBNAIL_SIZES[mediaSize]?.modifier : null;
  const indent = BODY_INDENTS[bodyIndent];

  // Centred content has to be centred on the CARD, not on what is left of the
  // row after the corner group, so the group comes out of the flow.
  const isHeaderCentered = isCentered && !hasLine && !isQuantity;
  // An empty corner must not take the row's gap, or the offer would sit a gap
  // in from whichever side the corner is on; nor may it wrap, which would open
  // a blank line.
  const isCornerEmpty = !hasLine && !hasCornerContent;

  const line = hasLine ? (
    <div
      className={clsx(
        'cp_order-bump-accept-line',
        `cp_order-bump-accept-line--${acceptEdge}`,
        `cp_order-bump-accept-line--${acceptAppearance}`,
        `cp_order-bump-accept-line--${acceptSide}`,
        {
          'cp_order-bump-accept-line--boxed': isBoxed,
          'cp_order-bump-accept-line--button-fit':
            acceptStyle === 'button' && buttonWidth === 'fit',
          'cp_order-bump-accept-line--no-selector': !hasSelector,
          'cp_order-bump-accept-line--button': acceptStyle === 'button',
        },
        // The pair keeps its source order, the control first so a keyboard
        // finds it where it reads, and the line reverses to send it right.
        acceptLineClassName({
          appearance: acceptAppearance,
          edge: acceptEdge,
          side: acceptSide,
          isButton: acceptStyle === 'button',
          hasSelector,
          isSelected,
          isBanner: hasBanner,
        }),
      )}
    >
      {indicator}
      {React.cloneElement(cta, { placement: 'line' })}
    </div>
  ) : null;

  const textColumn = (
    <div
      className={clsx(
        // A ring's width of padding, given back by the margin, so the amount
        // box's focus ring clears the column's clip at the sides.
        'cp_order-bump-text-wrapper -mx-1 flex min-w-0 flex-col px-1',
        COPY_COLUMN,
        {
          // Beside a picture the copy asks for 8rem and takes the rest of the
          // row. Where the picture, the gap and that minimum will not fit, the
          // row wraps and the copy drops under the picture at the card's width.
          'grow shrink basis-[8rem]': isMediaRow,
          'flex-1': !isMediaRow,
          'cp_order-bump-text-wrapper--centered items-center text-center': isCentered,
        },
      )}
    >
      {/* Beside the title the highlight sits at the end of the row while it
          fits and breaks onto a line of its own above it when it doesn't, as a
          price option's does. It rides with the TITLE rather than with the
          card, so a thumbnail keeps the row it leads and the highlight starts
          where the copy does. The title's 50% basis is the break trigger, so it
          scales with the card rather than a pixel breakpoint; wrap-reverse
          renders the wrapped line ABOVE the title. Stacked, the highlight leads
          on a line of its own whatever the width. The highlight's own
          alignment is in checkout.css. */}
      <div
        className={clsx('cp_order-bump-lines flex min-w-0 gap-x-4 gap-y-1', {
          'flex-wrap-reverse items-end': !isBadgeAbove,
          [String.raw`cp_order-bump-lines--stacked flex-col flex-nowrap [&>.cp\_order-bump-badge]:order-[-1]`]:
            isBadgeAbove,
          'items-start': isBadgeAbove && !isCentered,
          'cp_order-bump-lines--centered items-center': isCentered,
        })}
      >
        <div
          className={clsx('cp_order-bump-title-block flex min-w-0 flex-col grow shrink', {
            'basis-1/2': !isBadgeAbove,
            'basis-auto self-stretch': isBadgeAbove,
            'items-center': isCentered,
          })}
        >
          {titleBlock}
        </div>
        {badge}
      </div>
      {price}
      {descriptionPosition === 'column' && !dropsBody ? (
        <>
          {body}
          {amountInput}
        </>
      ) : null}
    </div>
  );

  let offerContent;
  if (mediaPosition === 'none' || isBanner) {
    offerContent = textColumn;
  } else if (mediaPosition === 'top') {
    offerContent = (
      <div className={clsx('flex flex-col gap-y-3', { 'items-center': isCentered })}>
        {media}
        {textColumn}
      </div>
    );
  } else {
    offerContent = (
      // Wraps rather than squeezes: the copy keeps a readable width and drops
      // under the picture when the two will not share the row.
      <div
        className={clsx('cp_order-bump-media-row flex flex-wrap items-start gap-x-4 gap-y-3', {
          'flex-row-reverse': mediaPosition === 'right',
          'justify-center': isCentered,
        })}
      >
        {media}
        {textColumn}
      </div>
    );
  }

  return (
    <>
      {isBanner ? media : null}
      {acceptEdge === 'top' ? line : null}

      <div
        className={clsx(
          'cp_order-bump-header flex items-start',
          isCornerEmpty ? 'gap-0' : 'flex-wrap gap-x-4 gap-y-2',
          {
            [String.raw`cp_order-bump-header--drops-body @container [&>.cp\_order-bump-description]:mt-0 [&>.cp\_order-bump-description]:grow [&>.cp\_order-bump-description]:shrink [&>.cp\_order-bump-description]:basis-full [&>.cp\_order-bump-amount]:basis-full`]:
              dropsBody,
            [`cp_order-bump-header--indent-${bodyIndent}`]: Boolean(bodyIndent),
            'cp_order-bump-header--centered relative': isHeaderCentered,
            'cp_order-bump-header--no-selector': isCornerEmpty,
            // The corner is last in the DOM whichever side it takes, so the
            // checkbox in it follows the offer in the tab order either way.
            'cp_order-bump-header--corner-left flex-row-reverse': isCornerLeft,
            'cp_order-bump-header--mark': !isQuantity && acceptStyle === 'mark',
            'cp_order-bump-header--switch': !isQuantity && acceptStyle === 'switch',
          },
          indent?.body,
        )}
      >
        <div
          className={clsx(
            'cp_order-bump-offer flex min-w-0 flex-col gap-y-2',
            // A narrow card cannot hold the offer and the corner side by side,
            // so the offer asks for 9rem and the corner wraps beneath it.
            isCornerEmpty ? 'flex-1' : ['grow shrink', indent ? indent.offer : 'basis-[9rem]'],
            {
              // Centred copy keeps clear of the control in the corner.
              'px-[calc(var(--cp_checkout-form-orderBumps-indicator-size)_+_0.75rem)]':
                isHeaderCentered && !isCornerEmpty && acceptStyle === 'mark',
              'px-[calc(var(--cp_checkout-form-orderBumps-switch-size)_*_1.75_+_0.75rem)]':
                isHeaderCentered && !isCornerEmpty && acceptStyle === 'switch',
            },
          )}
        >
          {hasLine || !showsCta || isCtaInCorner
            ? null
            : React.cloneElement(cta, { placement: 'offer' })}
          {offerContent}
        </div>

        {/* The selection control — the mark, or the stepper that replaces it,
            or the button or link that IS the call to action — in the corner
            and top-aligned, where a price option puts it, or centred on the
            row when the position says the middle. Not an empty corner once
            the line has it: the slot would have no width, but the row's gap
            beside it would still push the offer over. The gap inside the
            corner is the stepper's; beside a button it would only put the
            zero-width checkbox's worth of space between the two. */}
        {hasLine ? null : (
          <span
            className={clsx('cp_order-bump-accept-corner flex shrink-0', {
              'gap-x-4': !isCtaInCorner,
              'items-start': !isCornerCentered,
              'cp_order-bump-accept-corner--center items-center self-center': isCornerCentered,
              'cp_order-bump-accept-corner--left': isCornerLeft,
              'ml-auto': !isCornerEmpty,
              'mr-auto': isCornerLeft,
              absolute: isHeaderCentered,
              'top-0': isHeaderCentered && !isCornerCentered,
              'top-1/2 [transform:translateY(-50%)]': isHeaderCentered && isCornerCentered,
              'right-0': isHeaderCentered && !isCornerLeft,
              'left-0 right-auto': isHeaderCentered && isCornerLeft,
            })}
          >
            {indicator}
            {isCtaInCorner ? React.cloneElement(cta, { placement: 'corner' }) : null}
          </span>
        )}

        {/* Under the row, in the header so it can watch the header's width —
            the indent has to drop where the copy drops under the picture. */}
        {dropsBody ? (
          <>
            {body}
            {amountInput}
          </>
        ) : null}
      </div>

      {descriptionPosition === 'column' ? null : (
        <>
          {body}
          {amountInput}
        </>
      )}

      {acceptEdge === 'top' ? null : line}
    </>
  );
}

/**
 * The amount box on a pay-what-you-want bump, dressed from the Order bumps
 * PWYW subsection the way a variant option's is — suffix included, which the
 * bump reads as its interval when it recurs and its currency when it does not.
 */
function BumpAmountInput({
  offer,
  minimum,
  intervalText,
  enteredAmount,
  onAmountChange,
  disabled = false,
}) {
  const { theme, currencyLocale } = useCheckoutState();
  const suffixRef = useRef(null);

  const suffixText =
    offer.billingType === 'recurring' ? intervalText : (offer.currency ?? '').toUpperCase();
  const suffixEnabled = resolveInheritedEnabled(
    theme?.elements,
    'checkout.form.orderBumps.pwywSuffix',
    'checkout.pwyw.suffix',
  );
  const showSuffix = Boolean(suffixText) && suffixEnabled;

  // The suffix sits over the input's right edge, so the input has to clear it
  // by however wide it turns out to be.
  const [suffixWidth, setSuffixWidth] = useState(null);
  useIsomorphicLayoutEffect(() => {
    setSuffixWidth(showSuffix && suffixRef.current ? suffixRef.current.offsetWidth : null);
  }, [showSuffix, suffixText]);

  const id = `order-bump-amount-${offer.productId}`;
  const minimumInput = Number(formatCentsToDecimals(minimum, offer.currency));
  const placeholder = getPwywPlaceholder({
    minAmount: minimum,
    suggestedAmount: getBumpSuggestedPrice(offer),
    currency: offer.currency,
    locale: currencyLocale,
    labels: getThemeLabels(theme),
    showPrice: theme?.elements?.checkout?.form?.pwyw?.minPrice?.enabled !== false,
  });

  return (
    <div className="cp_order-bump-amount mt-2 w-full">
      {/* Inside the card's own <label>, a click here would reach the bump's
          checkbox and untick it, so it focuses the field itself. */}
      <label
        htmlFor={id}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          document.getElementById(id)?.focus();
        }}
        className={
          offer.price.label
            ? clsx(
                'cp_order-bump-pwyw-label cp_pwyw-label block break-words',
                '[color:var(--cp_pwyw-label-color,var(--cp_checkout-form-fields-label-color))]',
                '[font-family:var(--cp_pwyw-label-font-family,var(--cp_checkout-form-fields-label-font-family))]',
                '[font-style:var(--cp_pwyw-label-font-style,var(--cp_checkout-form-fields-label-font-style))]',
                '[font-weight:var(--cp_pwyw-label-font-weight,var(--cp_checkout-form-fields-label-font-weight))]',
                '[font-size:var(--cp_pwyw-label-font-size,var(--cp_checkout-form-fields-label-font-size))]',
                '[line-height:var(--cp_pwyw-label-line-height,var(--cp_checkout-form-fields-label-line-height))]',
                '[padding-bottom:var(--cp_pwyw-label-padding-bottom,var(--cp_checkout-form-fields-label-padding-bottom))]',
              )
            : 'sr-only'
        }
      >
        {offer.price.label || offer.title}
      </label>
      <div className="cp_order-bump-pwyw-input relative">
        <TextInput
          id={id}
          name={id}
          className={clsx(
            'cp_order-bump-amount-input cp_form-textinput',
            '[border-width:var(--cp_checkout-form-orderBumps-pwywInput-border-width)]',
            'rounded-[var(--cp_checkout-form-orderBumps-pwywInput-border-radius)]',
            '[padding:var(--cp_checkout-form-orderBumps-pwywInput-padding-y)_var(--cp_checkout-form-orderBumps-pwywInput-padding-x)]',
            '[font-family:var(--cp_checkout-form-orderBumps-pwywInput-font-family)]',
            '[font-style:var(--cp_checkout-form-orderBumps-pwywInput-font-style)]',
            '[font-weight:var(--cp_checkout-form-orderBumps-pwywInput-font-weight)]',
            '[font-size:max(var(--cp_input-min-font-size,0px),var(--cp_checkout-form-orderBumps-pwywInput-font-size))]',
            '[line-height:var(--cp_checkout-form-orderBumps-pwywInput-line-height)]',
            '[box-shadow:var(--cp_checkout-form-orderBumps-pwywInput-box-shadow,var(--cp_checkout-form-fields-input-box-shadow,0_1px_2px_0_rgb(0_0_0/5%)))]',
            'not-focus:bg-[var(--cp_checkout-form-orderBumps-pwywInput-background-color)]',
            'not-focus:text-[var(--cp_checkout-form-orderBumps-pwywInput-color)]',
            'not-focus:[border-color:var(--cp_checkout-form-orderBumps-pwywInput-border-color)]',
            'focus:bg-[var(--cp_checkout-form-orderBumps-pwywInput-focus-background-color)]',
            'focus:text-[var(--cp_checkout-form-orderBumps-pwywInput-focus-color)]',
            'focus:[border-color:var(--cp_checkout-form-orderBumps-pwywInput-focus-border-color)]',
            'focus:[box-shadow:0_0_0_3px_var(--cp_checkout-form-orderBumps-pwywInput-focus-border-color),var(--cp_checkout-form-orderBumps-pwywInput-box-shadow,var(--cp_checkout-form-fields-input-box-shadow,0_1px_2px_0_rgb(0_0_0/5%)))]',
            'placeholder:text-[var(--cp_checkout-form-orderBumps-pwywInput-placeholder-color)]',
            'placeholder:[font-family:var(--cp_checkout-form-orderBumps-pwywInput-font-family)]',
            'placeholder:[font-style:var(--cp_checkout-form-orderBumps-pwywInput-font-style)]',
            'placeholder:[font-weight:var(--cp_checkout-form-orderBumps-pwywInput-font-weight)]',
            'placeholder:[font-size:max(var(--cp_input-min-font-size,0px),var(--cp_checkout-form-orderBumps-pwywInput-font-size))]',
          )}
          style={
            showSuffix
              ? {
                  paddingRight: `calc(var(--cp_checkout-form-orderBumps-pwywInput-padding-x) + ${
                    suffixWidth ?? 30
                  }px + 0.4em)`,
                }
              : undefined
          }
          type="number"
          inputMode="decimal"
          step="any"
          min={minimumInput}
          value={enteredAmount ?? ''}
          placeholder={placeholder}
          disabled={disabled}
          // Stop the card's own click — the label's toggle, or the quantity
          // card's add-one — from firing as the buyer clicks into the field.
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
          // A focused number field takes the wheel as a step, so scrolling past it
          // changed the amount. Blurred, the wheel scrolls the page, as on the
          // product's own pay-what-you-want field.
          onWheel={event => event.currentTarget.blur()}
          onChange={event => onAmountChange(event.target.value)}
        />
        {showSuffix && (
          <span
            ref={suffixRef}
            // Its own style when set, then the base PWYW suffix, then the input's.
            className={clsx(
              'cp_paywhatyouwant-currency pointer-events-none absolute top-1/2 -translate-y-1/2',
              'right-[var(--cp_checkout-form-orderBumps-pwywInput-padding-x)]',
              'text-[var(--cp_checkout-form-orderBumps-pwywSuffix-color,var(--cp_pwyw-suffix-color,var(--cp_checkout-form-orderBumps-pwywInput-placeholder-color)))]',
              '[font-family:var(--cp_checkout-form-orderBumps-pwywSuffix-font-family,var(--cp_pwyw-suffix-font-family,var(--cp_checkout-form-orderBumps-pwywInput-font-family)))]',
              '[font-style:var(--cp_checkout-form-orderBumps-pwywSuffix-font-style,var(--cp_pwyw-suffix-font-style,var(--cp_checkout-form-orderBumps-pwywInput-font-style)))]',
              '[font-weight:var(--cp_checkout-form-orderBumps-pwywSuffix-font-weight,var(--cp_pwyw-suffix-font-weight,var(--cp_checkout-form-orderBumps-pwywInput-font-weight)))]',
              '[font-size:var(--cp_checkout-form-orderBumps-pwywSuffix-font-size,var(--cp_pwyw-suffix-font-size,var(--cp_checkout-form-orderBumps-pwywInput-font-size)))]',
            )}
          >
            {suffixText}
          </span>
        )}
      </div>
    </div>
  );
}

function OrderBumpCard({
  offer,
  layout,
  isGrid,
  quantity,
  enteredAmount,
  onSelect,
  onAmountChange,
  isSoldOut = false,
}) {
  const { currencyLocale, theme, isPreview } = useCheckoutState();

  const labels = getThemeLabels(theme);
  const isSelected = quantity > 0;
  const allowQuantity = offer.allowQuantity && !isSoldOut;
  const maxQuantity = getMaxBumpQuantity(offer);
  // On a PWYW bump `offer.price.amount` is the FLOOR the buyer must clear, not
  // the price — the server validates against the same figure.
  const isPayWhatYouWant = Boolean(offer.price.payWhatYouWant);
  const minimum = offer.price.amount ?? 0;

  const priceText = formatPrice({
    amount: offer.price.amount,
    currency: offer.currency,
    locale: currencyLocale,
    style: 'currency',
  });
  const intervalText =
    offer.billingType === 'recurring'
      ? getSubscriptionIntervalWithSlash(offer.interval, offer.intervalCount ?? 1, labels)
      : '';

  // Both sit on the price the offer already carries, and both read the way a
  // price option states them.
  const discountedFromText =
    !isPayWhatYouWant && offer.price.discountedFromPrice > 0
      ? formatPrice({
          amount: offer.price.discountedFromPrice,
          currency: offer.currency,
          locale: currencyLocale,
          style: 'currency',
        })
      : null;

  const setupFeeText =
    offer.price.setupFee > 0
      ? `${formatPrice({
          amount: offer.price.setupFee,
          currency: offer.currency,
          locale: currencyLocale,
          style: 'currency',
        })} ${labels.setupFeeText ?? 'setup fee'}`
      : null;

  const checkboxId = `order-bump-${offer.productId}`;

  // A stepper carries no checkbox to point a label at, so the card stops being
  // one — the swap Variants makes for a quantity variant option, which drops its
  // radio for a plain div and keeps the styles. Clicking the card still takes
  // the bump, one more each time: the whole card is the target on an ordinary
  // bump, and it would be a strange thing to lose by allowing a quantity.
  //
  // A pointer shortcut only, so it needs no key handler of its own — everything
  // it does is reachable from the stepper's own buttons and field, which are in
  // the tab order.
  const Card = allowQuantity ? 'div' : 'label';

  const hasCallToAction = resolveInheritedEnabled(
    theme?.elements,
    'checkout.form.orderBumps.callToAction',
  );

  // Button and link ARE the call to action — with the element hidden in the
  // theme they would have nothing to render, which would leave the card with
  // no visible way to accept it. Fall back to the default rather than ship that.
  //
  // A stepper is a control of its own, and the only one the card needs: the
  // count says whether the bump is taken and how many, so a button beside it
  // would be a second control saying the same thing. It takes the mark's place
  // (see BumpQuantity), and the style follows suit — the seller's copy, if any,
  // reads beside the stepper the way it reads beside a mark.
  const acceptStyle =
    ['button', 'link'].includes(layout.acceptStyle) && (!hasCallToAction || allowQuantity)
      ? DEFAULT_ACCEPT_STYLE
      : layout.acceptStyle;

  // A button or link with no copy would be a blank control, so those take the
  // theme's stand-in when the seller wrote no line for this product. A mark or
  // switch says how to accept by itself: beside it the stand-in is only filler,
  // so the copy there is the seller's or nothing.
  const isCtaTheControl = ['button', 'link'].includes(acceptStyle);
  const callToActionText =
    offer.callToActionText || (isCtaTheControl ? labels.orderBumpCallToActionText : undefined);

  // The arrangement is the theme's, copy or not: a bump without a call to
  // action keeps the same row or bar as its neighbours, so the cards on a page
  // line up however each one is filled in.
  const { acceptAppearance } = layout;
  // Beside the offer the top is a corner, and a corner is one side or the
  // other — the middle is a row's. The bottom is a line of its own even beside
  // the offer, so it keeps its centre. A row or bar, in turn, lies above or
  // below the offer and has no middle edge. The editor hides each where it
  // means nothing, and its dropdown then shows the default corner — so a theme
  // that carries one anyway lands there too, not on the nearest edge, or the
  // card would sit somewhere the editor never says.
  const isPositionHidden =
    acceptAppearance === 'offer'
      ? layout.acceptEdge === 'top' && layout.acceptSide === 'center'
      : layout.acceptEdge === 'center';
  const acceptEdge = isPositionHidden ? DEFAULT_ACCEPT_POSITION.edge : layout.acceptEdge;
  const acceptSide = isPositionHidden ? DEFAULT_ACCEPT_POSITION.side : layout.acceptSide;
  // A full-width picture leaves no room for a column beside it, so it becomes
  // the card's banner whatever the placement says.
  const isBanner = layout.mediaPosition !== 'none' && layout.mediaSize === 'full';
  const hasImage = Boolean(offer.product?.media?.[0]?.url);

  return (
    <Card
      htmlFor={allowQuantity ? undefined : checkboxId}
      onClick={
        allowQuantity
          ? event => {
              // The <label> card gets this exemption from the browser, which
              // suppresses label activation inside interactive content — so a
              // link or an accordion in the description is followed or opened
              // without taking the bump. The quantity card is a plain div, so
              // it has to make the same exception itself.
              if (event.target.closest?.(INTERACTIVE_CARD_CONTENT)) return;
              onSelect(Math.min(quantity + 1, maxQuantity));
            }
          : undefined
      }
      className={clsx(
        'cp_order-bump overflow-hidden border p-[var(--cp_price-picker-option-padding)]',
        // The control stays where it always sits, greyed, so a sold-out card
        // lines up with its neighbours; the badge under the title says why.
        isSoldOut
          ? String.raw`cp_order-bump--sold-out cursor-default [&_.cp\_order-bump-indicator]:opacity-40 [&_.cp\_order-bump-switch]:opacity-40 [&_.cp\_order-bump-cta]:opacity-40`
          : 'cursor-pointer',
        '[border-style:var(--cp_checkout-form-orderBumps-border-style)]',
        '[border-width:var(--cp_checkout-form-orderBumps-border-width)]',
        'rounded-[var(--cp_checkout-form-orderBumps-border-radius)]',
        // The checkbox behind every style is transparent, and under a button or
        // link it has no area at all, so the card stands in as its focus
        // target. Focus-visible, not focus-within: clicking a card focuses its
        // checkbox too, and a ring on every card the buyer touched read as a
        // second border.
        'has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-[var(--cp_checkout-form-orderBumps-border-color-selected)]',
        // In a grid the cards share a row height, so a trailing accept row is
        // pushed to the foot of each one by an auto margin on whatever comes
        // before it; the card grows rather than taking 100%, so a heading above
        // it takes its share of the cell first.
        isGrid
          ? String.raw`flex flex-auto flex-col [&>:has(+.cp\_order-bump-accept-line--bottom)]:mb-auto`
          : 'block',
        isSelected
          ? [
              'cp_order-bump--selected bg-[var(--cp_checkout-form-orderBumps-background-color-selected)] border-[var(--cp_checkout-form-orderBumps-border-color-selected)]',
              // Weight for the selected edge the way a price option draws it: an
              // INSET shadow, hugging the border from inside, so it reads as a
              // thicker border rather than a second one. A banner would cover
              // that on three sides, so a banner card wears it outside, where it
              // merges with the border into a single edge.
              isBanner && hasImage
                ? '[box-shadow:0_0_0_1px_var(--cp_checkout-form-orderBumps-border-color-selected)]'
                : '[box-shadow:inset_0_0_0_1px_var(--cp_checkout-form-orderBumps-border-color-selected)]',
            ]
          : 'bg-[var(--cp_checkout-form-orderBumps-background-color)] border-[var(--cp_checkout-form-orderBumps-border-color)]',
      )}
    >
      <OrderBumpCardLayout
        acceptAppearance={acceptAppearance}
        acceptEdge={acceptEdge}
        acceptSide={acceptSide}
        acceptStyle={acceptStyle}
        // A stepper is far too wide to sit over centred copy, so it keeps its
        // place in the row.
        isQuantity={allowQuantity}
        hasCta={hasCallToAction}
        mediaPosition={isBanner ? 'banner' : layout.mediaPosition}
        align={layout.align}
        descriptionPosition={layout.descriptionPosition}
        badgePosition={layout.badgePosition}
        buttonWidth={layout.buttonWidth}
        mediaSize={layout.mediaSize}
        hasMedia={layout.mediaPosition !== 'none' && hasImage}
        hasBanner={isBanner && hasImage}
        isSelected={isSelected}
        indicator={
          allowQuantity ? (
            <BumpQuantity offer={offer} quantity={quantity} max={maxQuantity} onChange={onSelect} />
          ) : (
            <BumpIndicator
              id={checkboxId}
              isSelected={isSelected}
              onToggle={() => onSelect(isSelected ? 0 : BUMP_QUANTITY)}
              style={acceptStyle}
              shape={layout.markShape}
              disabled={isSoldOut}
            />
          )
        }
        cta={
          <BumpCta
            text={callToActionText}
            style={acceptStyle}
            isSelected={isSelected}
            width={layout.buttonWidth}
            fill={layout.buttonFill}
            side={layout.align === 'center' ? 'center' : acceptSide}
          />
        }
        media={
          layout.mediaPosition === 'none' ? null : (
            <BumpMedia
              offer={offer}
              isPreview={isPreview}
              size={layout.mediaSize}
              ratio={layout.mediaRatio}
            />
          )
        }
        badge={<BumpBadge text={offer.price?.badge} icon={offer.price?.badgeIcon} />}
        titleBlock={
          <BumpTitleBlock offer={offer} soldOutText={isSoldOut ? labels.soldOutText : null} />
        }
        // A PWYW bump has no price to show: the buyer names it in the amount box.
        price={
          isPayWhatYouWant ? null : (
            <BumpPrice
              priceText={priceText}
              intervalText={intervalText}
              discountedFromText={discountedFromText}
              setupFeeText={setupFeeText}
            />
          )
        }
        body={
          <>
            <BumpFeatures
              features={offer.features}
              layout={layout.featuresLayout}
              isCentered={layout.align === 'center'}
            />
            <BumpBody
              id={`order-bump-description-${offer.productId}`}
              html={offer.descriptionHtml}
              isShortenable={offer.shortenDescription === true}
              isCentered={layout.align === 'center'}
              labels={labels}
            />
            <BumpCallout
              text={offer.calloutText}
              icon={offer.calloutIcon}
              isCentered={layout.align === 'center'}
            />
          </>
        }
        amountInput={
          isPayWhatYouWant ? (
            <BumpAmountInput
              offer={offer}
              minimum={minimum}
              intervalText={intervalText}
              enteredAmount={enteredAmount}
              onAmountChange={onAmountChange}
              disabled={isSoldOut}
            />
          ) : null
        }
      />
    </Card>
  );
}

// Before paint, so the step in between is never on screen. Form state follows
// the eligible set, not the visible one: hiding a bump keeps its selection.
function useSettleOrderBumps() {
  const { page, product, form, setForm } = useCheckoutState();
  const selectedPrice = useSelectedPrice();

  const eligibleOffers = getEligibleOrderBumpOffers({
    page,
    product,
    selectedPrice,
    ticketGroups: form?.ticketGroups,
    orderBumps: form?.orderBumps,
  });
  // Preselect and the quantity ceiling are part of the key, not just the id
  // list: flipping either in the editor does not change WHICH bumps are
  // eligible, so keying on ids alone left the preview un-ticked until it
  // remounted.
  const eligibleKey = eligibleOffers
    .map(
      offer =>
        `${String(offer.productId)}:${offer.preselected ? 1 : 0}:${getMaxBumpQuantity(offer)}`,
    )
    .join(',');
  const eligibleIds = eligibleOffers.map(offer => String(offer.productId));
  // Ticked bumps that are not eligible. Part of the effect's key because the
  // editor preview re-ticks every preselected placement after a save without
  // changing which bumps are eligible, and those ticks would count in the total.
  const staleKey = Object.entries(form?.orderBumps || {})
    .filter(([productId, quantity]) => quantity > 0 && !eligibleIds.includes(productId))
    .map(([productId]) => productId)
    .join(',');

  useIsomorphicLayoutEffect(() => {
    // A bump preselected on load is ticked here, so it takes its opening amount
    // here too: ticked with an empty box it would not be payable.
    const settle = latestForm => {
      const context = { page, product, selectedPrice, ticketGroups: latestForm?.ticketGroups };
      const settled = settleOrderBumps(latestForm?.orderBumps, context);
      return fillOrderBumpAmounts(
        settled,
        latestForm?.orderBumpAmounts,
        getEligibleOrderBumpOffers({ ...context, orderBumps: settled }),
      );
    };
    const isSettled = (latestForm, next) =>
      next.orderBumps === latestForm?.orderBumps &&
      next.orderBumpAmounts === latestForm?.orderBumpAmounts;
    if (isSettled(form, settle(form))) return;

    setForm(prevForm => {
      const next = settle(prevForm);
      return isSettled(prevForm, next) ? prevForm : { ...prevForm, ...next };
    });
  }, [eligibleKey, staleKey]);
}

/**
 * Whether OrderBumps renders anything: in the preview every offer (the ones the
 * checkout would drop are dimmed), on the checkout the shown ones. Layouts read
 * it to decide whether a column has anything in it, as they do for variants.
 *
 * Settles the selection too: a layout gating OrderBumps on this unmounts it in
 * the same commit its last card goes, before OrderBumps' own settle runs.
 */
export function useHasOrderBumps() {
  const { page, product, form, query, isPreview } = useCheckoutState();
  const selectedPrice = useSelectedPrice();
  useSettleOrderBumps();

  if (isPreview) return getOrderBumpOffers(page, product, page?.orderBumpProducts).length > 0;

  return (
    getCheckoutOrderBumpOffers({
      page,
      product,
      selectedPrice,
      ticketGroups: form?.ticketGroups,
      orderBumps: form?.orderBumps,
      query,
    }).length > 0
  );
}

export default function OrderBumps() {
  const { page, product, form, query, isPreview, theme } = useCheckoutState();
  const selectedPrice = useSelectedPrice();
  const { setOrderBump, setOrderBumpAmount } = useCheckoutOrderBumpFormHelpers();
  useSettleOrderBumps();

  const ticketGroups = form?.ticketGroups;
  const orderBumps = form?.orderBumps;

  // The preview renders every offer and dims the ones the live checkout would
  // drop; the checkout itself renders the shown ones and the sold-out ones.
  const rendered = isPreview
    ? getPreviewOrderBumpOffers({ page, product, selectedPrice, ticketGroups, orderBumps, query })
    : getCheckoutOrderBumpOffers({
        page,
        product,
        selectedPrice,
        ticketGroups,
        orderBumps,
        query,
      });

  if (!rendered.length) return null;

  const layout = getOrderBumpLayout(theme);
  const isGrid = layout.columns > 1;

  return (
    <StyledElement
      elementPath="checkout.form.orderBumps"
      className={clsx(
        'cp_order-bumps',
        // Phones get one column: two or three cards side by side clip their copy. An embed's iframe
        // is its own viewport, so narrow embeds stack too.
        isGrid
          ? 'cp_order-bumps--grid grid grid-cols-[repeat(var(--cp-order-bump-grid-cols,2),minmax(0,1fr))] max-sm:grid-cols-1 gap-2'
          : 'space-y-2',
      )}
      style={isGrid ? { '--cp-order-bump-grid-cols': layout.columns } : undefined}
    >
      {rendered.map(({ offer, isShown, isSoldOut }) => {
        const productId = String(offer.productId);
        const answer = form.orderBumps?.[productId];
        // A dimmed preview card shows the state the buyer WOULD arrive at, so a
        // preselected bump still reads as ticked while its rule is unmet — the
        // variant preview shows a dimmed variant filled with its preselected
        // option for the same reason. Display only: the form holds no answer for
        // it, so it counts towards nothing.
        const isDimmedPreselect = !isShown && answer === undefined && offer.preselected;
        const isPayWhatYouWant = Boolean(offer.price?.payWhatYouWant);
        const enteredAmount = form.orderBumpAmounts?.[productId];

        return (
          // Opacity only, as Variants.js does — no aria-hidden: the card holds a
          // real focusable checkbox, and hiding a focusable control from
          // assistive tech is worse than leaving it announced.
          // A dimmed card cannot be taken: a buyer would never see it, so a
          // click must not tick it into the preview's total.
          <div
            key={productId}
            className={clsx('cp_order-bump-item', {
              'flex flex-col': isGrid,
              'pointer-events-none opacity-25': !isShown,
            })}
          >
            <BumpCardHeading text={offer.headingText} isCentered={layout.align === 'center'} />
            <OrderBumpCard
              offer={offer}
              layout={layout}
              isGrid={isGrid}
              quantity={isSoldOut ? 0 : isDimmedPreselect ? BUMP_QUANTITY : answer || 0}
              enteredAmount={enteredAmount}
              onSelect={
                isShown && !isSoldOut
                  ? quantity => {
                      // Added means added with an amount: the box opens at the
                      // suggested price, else the minimum. With neither there is
                      // nothing to open at, so the buyer is sent to type one.
                      if (isPayWhatYouWant && quantity > 0 && isBlankAmount(enteredAmount)) {
                        const amount = getDefaultBumpAmount(offer);
                        if (amount === null) {
                          document.getElementById(`order-bump-amount-${offer.productId}`)?.focus();
                          return;
                        }
                        setOrderBumpAmount(offer.productId, amount);
                      }
                      setOrderBump(offer.productId, quantity);
                      // Off, the box empties so no figure sits on a bump not taken.
                      if (isPayWhatYouWant && quantity === 0) {
                        setOrderBumpAmount(offer.productId, '');
                      }
                    }
                  : noop
              }
              onAmountChange={
                isShown && !isSoldOut
                  ? amount => {
                      setOrderBumpAmount(offer.productId, amount);
                      // The figure and the tick never disagree: typing adds the
                      // bump, clearing the box takes it off.
                      const isTicked = answer > 0;
                      if (isBlankAmount(amount) && isTicked) setOrderBump(offer.productId, 0);
                      if (!isBlankAmount(amount) && !isTicked) {
                        setOrderBump(offer.productId, BUMP_QUANTITY);
                      }
                    }
                  : noop
              }
              isSoldOut={isSoldOut}
            />
          </div>
        );
      })}
    </StyledElement>
  );
}
